// Within-conference ranking, which also supplies the tournament seed
// order for the projected bracket: points (3W + 1T), then tiebreak.

import type { ConferenceGame } from "./types";

export type RankableTeam = {
  teamSlug: string;
  conferenceRecord: { wins: number; losses: number; ties: number };
  conferenceGoalsFor: number;
  conferenceGoalsAgainst: number;
};

type Result = { goalsFor: number; goalsAgainst: number };

// Every result per team, keyed by opponent. A pair can meet more than
// once, so each opponent holds a list.
type Results = Map<string, Map<string, Result[]>>;

// Orders a group level on points. `seedOrder` is the whole conference
// by points then slug, which is the seed order the SEC's "highest-seeded
// common opponent" steps walk.
type Tiebreak = (
  group: RankableTeam[],
  results: Results,
  seedOrder: string[],
) => RankableTeam[];

function tablePoints(t: RankableTeam): number {
  return t.conferenceRecord.wins * 3 + t.conferenceRecord.ties;
}

function matchPoints(r: Result): number {
  if (r.goalsFor > r.goalsAgainst) return 3;
  return r.goalsFor === r.goalsAgainst ? 1 : 0;
}

function bySlug(a: RankableTeam, b: RankableTeam): number {
  return a.teamSlug.localeCompare(b.teamSlug);
}

function buildResults(games: ConferenceGame[]): Results {
  const results: Results = new Map();
  const add = (team: string, opponent: string, r: Result) => {
    let byOpponent = results.get(team);
    if (!byOpponent) {
      byOpponent = new Map();
      results.set(team, byOpponent);
    }
    const list = byOpponent.get(opponent);
    if (list) list.push(r);
    else byOpponent.set(opponent, [r]);
  };
  for (const g of games) {
    add(g.homeSlug, g.awaySlug, { goalsFor: g.homeScore, goalsAgainst: g.awayScore });
    add(g.awaySlug, g.homeSlug, { goalsFor: g.awayScore, goalsAgainst: g.homeScore });
  }
  return results;
}

function resultsAgainst(results: Results, team: string, opponents: Iterable<string>): Result[] {
  const out: Result[] = [];
  for (const o of opponents) out.push(...(results.get(team)?.get(o) ?? []));
  return out;
}

function sum(rs: Result[], f: (r: Result) => number): number {
  return rs.reduce((acc, r) => acc + f(r), 0);
}

// compareVectors orders numeric keys lexicographically, descending.
function compareVectors(a: number[], b: number[]): number {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const d = (b[i] ?? 0) - (a[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

// partition splits a group into levels by a key, best level first.
function partition(group: RankableTeam[], key: (t: RankableTeam) => number[]): RankableTeam[][] {
  const keyed = group.map((t) => ({ t, k: key(t) }));
  keyed.sort((a, b) => compareVectors(a.k, b.k));
  const levels: RankableTeam[][] = [];
  let prev: number[] | undefined;
  for (const { t, k } of keyed) {
    if (prev && compareVectors(prev, k) === 0) levels[levels.length - 1].push(t);
    else levels.push([t]);
    prev = k;
  }
  return levels;
}

function commonOpponents(group: RankableTeam[], results: Results): Set<string> {
  const inGroup = new Set(group.map((t) => t.teamSlug));
  const common = new Set<string>();
  const [first, ...rest] = group;
  for (const o of results.get(first.teamSlug)?.keys() ?? []) {
    if (inGroup.has(o)) continue;
    if (rest.every((t) => results.get(t.teamSlug)?.has(o))) common.add(o);
  }
  return common;
}

// commonOpponentTiebreak orders teams level on points, using the SEC's
// published procedure (weekly soccer release, Oct. 26, 2026) for every
// conference. Most conferences publish their own steps, some of which need
// data we lack (RPI, cards), so this is an approximation outside the SEC.
// It was chosen by testing against 2026 published seeds: it matched 10 of
// 11 conferences, where a fewer-losses first step, which no written
// procedure uses, got three wrong.
//
// Two teams: head-to-head; then points, goal difference and goals scored
// against common opponents; then results, goal difference and goals scored
// against each common opponent from the highest seed down; then a coin
// flip. Three or more: mini-table if all have met, or a team that beat all
// the others goes top; otherwise the two-team steps from common opponents
// on. Any split restarts on the smaller groups, so a tie cut to two teams
// starts over at head-to-head as the procedure requires. The coin flip and
// commissioner's draw are stood in for by slug order.
const commonOpponentTiebreak: Tiebreak = (group, results, seedOrder) => {
  if (group.length < 2) return group;

  const slugs = group.map((t) => t.teamSlug);
  const allMet = group.every((t) =>
    slugs.every((s) => s === t.teamSlug || results.get(t.teamSlug)?.has(s)),
  );
  const common = commonOpponents(group, results);
  const commonBySeed = seedOrder.filter((s) => common.has(s));
  const against = (t: RankableTeam, opponents: Iterable<string>) =>
    resultsAgainst(results, t.teamSlug, opponents);
  const peers = (t: RankableTeam) => slugs.filter((s) => s !== t.teamSlug);
  const perOpponent = (t: RankableTeam, f: (rs: Result[]) => number) =>
    commonBySeed.map((o) => f(against(t, [o])));
  const goalDiff = (r: Result) => r.goalsFor - r.goalsAgainst;

  const keys: ((t: RankableTeam) => number[])[] = [
    // A pair that never met scores 0-0 here and falls through.
    ...(group.length === 2 || allMet
      ? [(t: RankableTeam) => [sum(against(t, peers(t)), matchPoints)]]
      : [
          (t: RankableTeam) => [
            peers(t).every((p) =>
              against(t, [p]).some((r) => r.goalsFor > r.goalsAgainst),
            )
              ? 1
              : 0,
          ],
        ]),
    (t) => [sum(against(t, common), matchPoints)],
    (t) => [sum(against(t, common), goalDiff)],
    (t) => [sum(against(t, common), (r) => r.goalsFor)],
    (t) => perOpponent(t, (rs) => sum(rs, matchPoints)),
    (t) => perOpponent(t, (rs) => sum(rs, goalDiff)),
    (t) => perOpponent(t, (rs) => sum(rs, (r) => r.goalsFor)),
  ];

  for (const key of keys) {
    const levels = partition(group, key);
    if (levels.length > 1) {
      // Every level is strictly smaller than the group, so this
      // recursion ends within the conference's team count.
      return levels.flatMap((level) => commonOpponentTiebreak(level, results, seedOrder));
    }
  }
  return [...group].sort(bySlug);
};

// rankConference returns one conference's teams, best first. O(n log n)
// to order by points plus the tiebreak on each level, which is bounded by
// the conference size and the games those teams played.
export function rankConference(
  teams: RankableTeam[],
  games: ConferenceGame[],
): RankableTeam[] {
  const results = buildResults(games);
  const ordered = [...teams].sort(
    (a, b) => tablePoints(b) - tablePoints(a) || bySlug(a, b),
  );
  const seedOrder = ordered.map((t) => t.teamSlug);

  const ranked: RankableTeam[] = [];
  let i = 0;
  while (i < ordered.length) {
    let j = i + 1;
    while (j < ordered.length && tablePoints(ordered[j]) === tablePoints(ordered[i])) j++;
    ranked.push(...commonOpponentTiebreak(ordered.slice(i, j), results, seedOrder));
    i = j;
  }
  if (ranked.length !== teams.length) {
    throw new Error(`invariant: ranked ${ranked.length} of ${teams.length} teams`);
  }
  return ranked;
}
